<template>
  <div class="lei-row" :class="{ 'lei-row--stale': isStale }">
    <div class="lei-row__head" :class="{ 'lei-row__head--edit': reorderMode }">
      <span v-if="reorderMode" class="lei-drag" :class="{ 'lei-drag--off': !channelRecord }"
            :title="channelRecord ? 'Drag to reorder' : 'Not found in channels, can\'t be reordered'">⋮⋮</span>
      <span class="lei-row__num lei-mono">{{ orderLabel }}</span>
      <div class="lei-row__line lei-row__line--top">
      <div class="lei-row__id">
        <span class="lei-row__name lei-mono">{{ channel.channelName || '—' }}</span>
        <span v-if="channel.language" class="lei-row__lang">{{ channel.language }}</span>
      </div>
      <div class="lei-row__flags">
        <span v-if="!channelRecord && channelRecordsLoaded" class="lei-row__missing">not found in channels</span>
        <template v-else-if="channelRecord">
          <span class="lei-flag" :class="channelRecord.status ? 'lei-flag--on' : 'lei-flag--off'">{{ channelRecord.status ? 'status on' : 'status off' }}</span>
          <span class="lei-flag" :class="channelRecord.active ? 'lei-flag--on' : 'lei-flag--off'">{{ channelRecord.active ? 'active' : 'inactive' }}</span>
          <span v-if="mismatch" class="lei-mismatch">{{ mismatch }}</span>
        </template>
      </div>
      </div>
      <div class="lei-row__line lei-row__line--bottom">
      <span v-if="channelRecord" class="lei-row__views lei-mono" :class="{ 'lei-muted': !channelRecord.status }">
        <template v-if="channelRecord.status"><b>{{ viewsLabel }}</b> live viewers</template>
        <template v-else>— live viewers</template>
      </span>
      <div class="lei-row__actions">
        <button v-if="linked" type="button" class="lei-btn lei-btn--sm" :class="{ 'is-open': infoOpen }" @click="toggleInfo">
          {{ infoOpen ? 'Less info' : 'More info' }}
        </button>
        <button v-if="channel.channelName" type="button" class="lei-btn lei-btn--sm lei-btn--icon" title="Open Kibana dashboard" @click="openKibana">
          <img src="@/assets/chart-line.svg" alt="Kibana" />
        </button>
      </div>
      </div>
    </div>
    <div class="lei-strip" :class="'lei-strip--' + tone">
      <div class="lei-strip__state">
        <span class="lei-badge" :class="'lei-badge--' + tone">{{ badgeLabel }}</span>
        <span v-if="stateNote" class="lei-strip__note">{{ stateNote }}</span>
      </div>
      <div v-if="state === 'not-linked'" class="lei-strip__body lei-muted">No encoder or stream id set on this channel</div>
      <template v-else>
        <div class="lei-strip__body">
          <span class="lei-strip__name">{{ channel.encStreamName }}</span> <span v-if="statsLine" class="lei-strip__stats lei-mono">{{ statsLine }}</span>
        </div>
        <div class="lei-strip__ids lei-mono">
          <span>{{ channel.encoder }}<template v-if="channel.origin"> &rarr;</template></span> <span v-if="channel.origin">{{ channel.origin }}</span>
        </div>
      </template>
    </div>
    <div v-if="alarmList.length" class="lei-row__config">
      <div v-for="a in alarmList" :key="a.type" class="lei-info__alarm">since {{ a.since }} · {{ a.text }}</div>
    </div>
    <div v-if="configIssues.length" class="lei-row__config">
      <div v-for="(issue, idx) in configIssues" :key="idx" class="lei-info__warn">{{ issue }}</div>
    </div>
    <div v-if="infoOpen" class="lei-info">
      <div v-if="infoLoading" class="lei-muted">Loading from {{ channel.encoder }}…</div>
      <div v-else-if="infoError" class="lei-row__error">{{ infoError }}</div>
      <!-- after Refresh: read now from the encoder -->
      <template v-else-if="info">
        <div v-if="outputMismatch" class="lei-info__warn">{{ outputMismatch }}</div>
        <div class="lei-info__grid">
          <span class="lei-muted">Status</span><span>{{ info.status || '—' }}</span>
          <span class="lei-muted">Stream</span><span class="lei-mono">{{ info.name || '—' }} <span class="lei-muted">#{{ info.id || channel.encStreamId }}</span></span>
          <span class="lei-muted">Inputs</span>
          <span>
            <span v-if="!inputs.length" class="lei-muted">none</span>
            <span v-for="(inp, idx) in inputs" :key="idx" class="lei-info__url lei-mono">{{ idx + 1 }}: {{ inp }}</span>
          </span>
          <span class="lei-muted">Output</span><span class="lei-info__url lei-mono">{{ info.output || '—' }}</span>
        </div>
      </template>
      <!-- otherwise: what B-API kept when it last read the stream (start, adopt, restart) -->
      <div v-else-if="!feedUrlsRead" class="lei-muted">
        Not read by B-API: it reads the URLs when it starts or takes over the stream, or sees it restart. Refresh reads them from the encoder.
      </div>
      <div v-else class="lei-info__grid">
        <span class="lei-muted">Status</span><span>{{ stat ? stat.status : 'stopped' }}</span>
        <span class="lei-muted">Stream</span><span class="lei-mono">{{ (stat && stat.streamName) || channel.encStreamName || '—' }} <span class="lei-muted">#{{ channel.encStreamId }}</span></span>
        <span class="lei-muted">Inputs</span>
        <span>
          <span v-if="!feedInputs.length" class="lei-muted">none</span>
          <span v-for="(inp, idx) in feedInputs" :key="idx" class="lei-info__url lei-mono">{{ idx + 1 }}: {{ inp || '—' }}</span>
        </span>
        <span class="lei-muted">Outputs</span>
        <span>
          <span v-if="!feedOutputs.length" class="lei-muted">none</span>
          <span v-for="(out, idx) in feedOutputs" :key="idx" class="lei-info__url lei-mono">{{ idx + 1 }}: {{ out || '—' }}</span>
        </span>
      </div>
      <div v-if="!infoLoading" class="lei-info__foot lei-muted">
        <template v-if="info">read from {{ channel.encoder }} at {{ infoAt }}</template>
        <template v-else-if="feedUrlsRead">from B-API, read at {{ feedReadAt }}</template>
        <button type="button" class="lei-btn lei-btn--sm" :disabled="infoLoading" @click="loadInfo" title="Read the stream now from the encoder">Refresh</button>
      </div>
    </div>
  </div>
</template>

<script>
import XaccelService from '@/services/xaccel-service';
import {
  isLinkedChannel, isStreamRunning, formatBitrate, formatUptime, formatSpeed, formatStatInt,
  extractStreamNameFromUrl, kibanaDashboardUrl, encStreamIdentifier
} from '@/utils/bapi-functions';

const EMPTY_STATS = '— · — fps · — · — · — errors';

export default {
  name: 'LiveChannelRow',
  emits: ['info-loaded'],
  props: {
    channel: { type: Object, required: true },
    encoderStateEntry: { type: Object, default: null },
    channelRecord: { type: Object, default: null },
    channelRecordsLoaded: { type: Boolean, default: false },
    reorderMode: { type: Boolean, default: false },
    configIssues: { type: Array, default: () => [] },
    // B-API's record of this feed (GET /calendar/state), null = none (window not open, or B-API has no data)
    feed: { type: Object, default: null }
  },
  data() {
    return {
      infoOpen: false,
      infoLoading: false,
      infoError: '',
      info: null,
      infoAt: ''
    };
  },
  computed: {
    linked() {
      return isLinkedChannel(this.channel);
    },
    orderLabel() {
      const n = this.channelRecord?.orderNumber;
      return n !== null && n !== undefined ? n : '—';
    },
    viewsLabel() {
      return Number(this.channelRecord?.liveViews || 0).toLocaleString();
    },
    stat() {
      const entry = this.encoderStateEntry;
      if (!entry || !entry.stats) return null;
      return entry.stats.get(String(this.channel.encStreamId || '').trim()) || null;
    },
    running() {
      return !!this.stat && isStreamRunning(this.stat.status);
    },
    state() {
      if (!this.linked) return 'not-linked';
      const entry = this.encoderStateEntry;
      if (!entry || entry.pending) return 'loading';
      if (this.stat) return this.running ? 'streaming' : 'stopped';
      // B-API lists only the streams that are not stopped: missing from a good read = stopped
      return entry.unreachable ? 'unreachable' : 'stopped';
    },
    isStale() {
      return !!this.stat && !!this.encoderStateEntry && this.encoderStateEntry.unreachable;
    },
    tone() {
      switch (this.state) {
        case 'streaming': return 'ok';
        case 'stopped': return 'bad';
        case 'unreachable': return 'warn';
        default: return 'none';
      }
    },
    stateLabel() {
      const labels = {
        'streaming': 'streaming',
        'stopped': 'stopped',
        'unreachable': 'unreachable',
        'not-linked': 'not linked',
        'loading': 'loading…'
      };
      return labels[this.state];
    },
    badgeLabel() {
      return this.state === 'streaming' ? 'live' : this.stateLabel;
    },
    stateNote() {
      if (this.isStale) {
        const at = this.encoderStateEntry.lastOkAt;
        return at ? 'stale · ' + at.toLocaleTimeString(undefined, { hour12: false }) : 'stale';
      }
      if (this.state === 'unreachable') return this.encoderStateEntry?.notRead ? 'not read by B-API' : 'no last-known values';
      return '';
    },
    mismatch() {
      if (!this.channelRecord || !['streaming', 'stopped'].includes(this.state)) return '';
      if (this.channelRecord.status && !this.running) return 'status on, encoder ' + this.stateLabel;
      if (!this.channelRecord.status && this.running) return 'encoder streaming, status off';
      return '';
    },
    statsLine() {
      if (this.state === 'loading') return '';
      if (!this.running) return EMPTY_STATS;
      const s = this.stat;
      return [
        formatBitrate(s.bitrate),
        formatStatInt(s.fps) + ' fps',
        formatUptime(s.uptime),
        formatSpeed(s.speed),
        formatStatInt(s.errors) + ' errors'
      ].join(' · ');
    },
    alarmList() {
      const alarms = this.feed?.alarms || {};
      return Object.entries(alarms).map(([type, a]) => ({
        type,
        text: a.text,
        since: new Date(a.openedAt).toLocaleTimeString(undefined, { hour12: false })
      }));
    },
    feedUrlsRead() {
      return !!this.feed && !!this.feed.urlsReadAt;
    },
    feedInputs() {
      return this.feed?.inputUrls || [];
    },
    feedOutputs() {
      return this.feed?.outputUrls || [];
    },
    feedReadAt() {
      return this.feed?.urlsReadAt ? new Date(this.feed.urlsReadAt).toLocaleTimeString(undefined, { hour12: false }) : '';
    },
    inputs() {
      const list = Array.isArray(this.info?.inputs) ? this.info.inputs : [];
      return list.map(inp => (typeof inp === 'string' ? inp : (inp?.url || JSON.stringify(inp))));
    },
    outputMismatch() {
      if (!this.info) return '';
      if (!this.info.output) return 'Encoder stream has no output URL.';
      const outName = extractStreamNameFromUrl(this.info.output);
      if (outName && this.channel.channelName && outName !== this.channel.channelName) {
        return `Encoder output points to ${outName}, this channel is ${this.channel.channelName}.`;
      }
      return '';
    }
  },
  methods: {
    // Opening shows what B-API kept (no encoder call); only Refresh reads the encoder.
    toggleInfo() {
      this.infoOpen = !this.infoOpen;
      if (!this.infoOpen) {
        this.info = null;
        this.infoError = '';
      }
    },
    loadInfo() {
      this.infoLoading = true;
      this.infoError = '';
      XaccelService.getXaccelStreamMetadata(this.channel.encoder.trim(), encStreamIdentifier(this.channel.encStreamId))
        .then((res) => {
          this.info = res || {};
          // The page uses this read for its setup check while it is newer than B-API's.
          this.$emit('info-loaded', this.info);
          this.infoAt = new Date().toLocaleTimeString(undefined, { hour12: false });
        })
        .catch((err) => {
          console.error('LiveChannelRow: error loading stream info', err);
          this.infoError = 'Could not load stream info from ' + this.channel.encoder + '.';
        })
        .finally(() => {
          this.infoLoading = false;
        });
    },
    openKibana() {
      const url = kibanaDashboardUrl(this.channel.channelName);
      if (url) window.open(url, '_blank');
    }
  }
};
</script>
<style src="../assets/style.css"></style>
